import React, { useCallback, useContext, useRef } from "react";
import { ClientObjectUpdate, ENTITY, OBJECT_TYPE, ServerObjectMessage } from "./eventsDispatcher/eventTypes";
import { DataBaseContext } from "./storage/db.";

export interface PendingUpdate {
    id: number
    timestamp: number
    update: ServerObjectMessage | ClientObjectUpdate
}

const usePendingUpdates = () => {
    const db = useContext(DataBaseContext);
    const syncUpdates = useRef<ServerObjectMessage[]>([]); // список аплейтов, которые пришли с сервера во время sync

    const storeUpdate = useCallback(async (update: ClientObjectUpdate | ServerObjectMessage): Promise<number> => {
        return new Promise((resolve, reject) => {
            if (!db)
                reject(new Error('db not initiated'))
            else {

                const transaction = db.db.transaction(['PENDINGUPDATES'], 'readwrite');
                const store = transaction.objectStore('PENDINGUPDATES');
                const updateToStore = { ...update, sourceWidget: 'offline' }
                let request = store.put({
                    update: updateToStore,
                    timestamp: Math.round(new Date().getTime() / 1000)
                });
                request.onsuccess = () => resolve(request.result as number);
                request.onerror = () => reject(request.error);
            }
        })
    }, [db.db]);

    const updatePendingUpdate = useCallback(async (pendingUpdate: PendingUpdate): Promise<null> => {
        return new Promise((resolve, reject) => {
            if (!db)
                reject(new Error('db not initiated'))
            else {

                const transaction = db.db.transaction(['PENDINGUPDATES'], 'readwrite');
                const store = transaction.objectStore('PENDINGUPDATES');
                const request = store.put(pendingUpdate);

                request.onsuccess = () => {
                    resolve(null)
                };
                request.onerror = () => reject(request.error);
            }
        })
    }, [db.db]);

    const getPendingUpdates = useCallback(async (): Promise<PendingUpdate[]> => {
        return new Promise((resolve, reject) => {
            if (!db)
                reject(new Error('db not initiated'))
            else {

                const transaction = db.db.transaction(['PENDINGUPDATES'], 'readwrite');
                const store = transaction.objectStore('PENDINGUPDATES');
                const request = store.getAll()
                request.onsuccess = (evt) => {
                    const pendingUpdates: PendingUpdate[] = request.result;

                    resolve(pendingUpdates.sort((a, b) => a.timestamp - b.timestamp));
                };
                request.onerror = () => reject(request.error);
            }

        })
    }, [db?.db]);

    const removePendingUpdate = useCallback(async (id: number) => {
        return new Promise((resolve, reject) => {
            if (!db)
                reject(new Error('db not initiated'))
            else {

                const transaction = db.db.transaction(['PENDINGUPDATES'], 'readwrite');
                const store = transaction.objectStore('PENDINGUPDATES');
                const request = store.delete(id);

                request.onsuccess = (evt) => {
                    resolve(null);
                };
                request.onerror = () => reject(request.error);
            }

        })
    }, [db?.db])

    const deletePendingUpdatesForObject = useCallback(async (uuid: string, object_type: OBJECT_TYPE) => {
        return new Promise((resolve, reject) => {
            if (!db)
                reject(new Error('db not initiated'))
            else {
                const transaction = db.db.transaction(['PENDINGUPDATES'], 'readwrite');
                const store = transaction.objectStore('PENDINGUPDATES');
                const request = store.getAll();

                request.onsuccess = () => {
                    const pendingUpdates: PendingUpdate[] = request.result || [];
                    const idsToDelete = pendingUpdates
                        .filter(pu => pu.update.uuid === uuid && pu.update.object_type === object_type)
                        .map(pu => pu.id);

                    if (idsToDelete.length === 0) {
                        resolve(null);
                        return;
                    }

                    idsToDelete.map(id => store.delete(id));
                    transaction.oncomplete = () => resolve(null);
                    transaction.onerror = () => reject(transaction.error);
                };
                request.onerror = () => reject(request.error);
            }
        })
    }, [db?.db])

    const preparePendingUpdates = useCallback(async (): Promise<PendingUpdate[]> => {
        // Подготавливает список апдейтов к отправке:
        // На основе сообщений от сервера, убирает те поля в апдейтах, которые были изменены на сервере.
        // сервер - источник правды
        let pendingUpdates = await getPendingUpdates();
        const puDeleteList: number[] = [];
        const puUpdateList: PendingUpdate[] = [];
        syncUpdates.current.map(serverUpdate => {
            if (serverUpdate.toDelete) {
                // Объект удален, все апдейты с ним нужно также удалить
                pendingUpdates.map(pu => {
                    if (pu.update.uuid === serverUpdate.uuid && pu.update.object_type === serverUpdate.object_type) {
                        puDeleteList.push(pu.id)
                    }
                })
            } else {
                // обновление объекта. В этом случае стираем свойства апдейтов
                pendingUpdates.map((pu, index) => {
                    if (pu.update.uuid === serverUpdate.uuid && pu.update.object_type === serverUpdate.object_type) {
                        const newPu = { ...pu };
                        const newData = { ...pu.update.data };
                        Object.keys(serverUpdate.data).map(key => {
                            delete newData[key as keyof ENTITY];
                        })
                        newPu.update.data = newData;
                        if (Object.keys(newData).length === 0) {
                            puDeleteList.push(pu.id)
                        } else {
                            puUpdateList.push(newPu);
                        }

                    }

                })
            }
        })
        await Promise.all(puDeleteList.map(id => removePendingUpdate(id)));
        await Promise.all(puUpdateList.map(pu => updatePendingUpdate(pu)));
        syncUpdates.current = [];
        return pendingUpdates

    }, [db?.db, getPendingUpdates, removePendingUpdate, updatePendingUpdate]);

    const storeSyncUpdate = useCallback((update: ServerObjectMessage) => {
        syncUpdates.current.push(update);
    }, []);

    return {
        storeUpdate, getPendingUpdates, removePendingUpdate, storeSyncUpdate, preparePendingUpdates, deletePendingUpdatesForObject
    }
}

export default usePendingUpdates;